/**
 * Aris — Motor de comandos por texto (sin IA).
 *
 * Layout: barra centrada verticalmente. Los widgets aparecen apilados
 * ARRIBA de la barra (más recientes cerca de la barra). Fullscreen API
 * al montar para ocultar la UI del navegador.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePatientCache } from "../lib/patientCache";
import {
  parseCommand,
  runParsedCommand,
  HELP_COMMANDS,
} from "../lib/arisCommands";
import { toast } from "sonner";
import {
  X,
  Send,
  Sparkles,
  MessageSquare,
  Phone,
  Calendar,
  FileText,
  User,
  IdCard,
  Stethoscope,
} from "@/components/icons";

const ageFromBirth = (iso) => {
  if (!iso) return null;
  try {
    const b = new Date(iso);
    const diff = Date.now() - b.getTime();
    return Math.floor(diff / (365.25 * 24 * 3600 * 1000));
  } catch {
    return null;
  }
};

const exitFsAndGo = (navigate, path) => {
  navigate(path);
};

const formatApptDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const day = d.toLocaleDateString("es-ES", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
    const time = d.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${day} · ${time}`;
  } catch {
    return iso;
  }
};

const formatShortDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function Aris() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { search, ready: cacheReady, patients } = usePatientCache();

  const [input, setInput] = useState("");
  const [widgets, setWidgets] = useState([]); // stack: [{id, kind, ...}]
  const [activePatient, setActivePatient] = useState(null);
  const [consultation, setConsultation] = useState(null);
  const [lastIcd, setLastIcd] = useState(null); // {cie10, cie11} — usado al setear diagnóstico
  const [prescriptions, setPrescriptions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [diagnoses, setDiagnoses] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const inputRef = useRef(null);
  const stackRef = useRef(null);

  // Fetch de las 3 entidades adicionales para el autosuggest global (pacientes
  // ya vienen del cache). Fallback silencioso si algo falla.
  useEffect(() => {
    if (!user || user.role !== "doctor") return;
    let cancelled = false;
    (async () => {
      try {
        const [rx, ap, dx] = await Promise.all([
          api.get("/prescriptions").catch(() => ({ data: [] })),
          api.get("/appointments").catch(() => ({ data: [] })),
          api.get("/diagnoses").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setPrescriptions(rx.data || []);
        setAppointments(ap.data || []);
        setDiagnoses(dx.data || []);
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Diccionario id → paciente para armar los subtitulos del autosuggest
  const patientById = useMemo(() => {
    const m = new Map();
    patients.forEach((p) => m.set(p.id, p));
    return m;
  }, [patients]);

  // Resultados del autosuggest global — pacientes, recetas, citas, dx.
  const suggestGroups = useMemo(() => {
    const q = input.trim().toLowerCase();
    if (!q || q.length < 2) return [];
    const clean = q.replace(/\s|-/g, "");
    const norm = (s) => (s || "").toString().toLowerCase();

    const pats = patients
      .filter((p) => {
        const name = norm(p.full_name);
        const phone = norm(p.phone).replace(/\s|-/g, "");
        const doc = norm(p.document_id);
        return name.includes(q) || phone.includes(clean) || doc.includes(q);
      })
      .slice(0, 3);

    const rxs = prescriptions
      .filter((r) => {
        const pat = patientById.get(r.patient_id);
        const name = norm(pat?.full_name);
        const meds = (r.items || []).map((it) => norm(it.medication || it.medication_name)).join(" ");
        return name.includes(q) || meds.includes(q);
      })
      .slice(0, 3);

    const appts = appointments
      .filter((a) => {
        const pat = patientById.get(a.patient_id);
        const name = norm(pat?.full_name);
        const reason = norm(a.reason || a.notes);
        return name.includes(q) || reason.includes(q);
      })
      .slice(0, 3);

    const dxs = diagnoses
      .filter((d) => {
        const text = norm(d.diagnosis);
        const c10 = norm(d.cie10);
        const c11 = norm(d.cie11);
        const pat = patientById.get(d.patient_id);
        const name = norm(pat?.full_name);
        return text.includes(q) || c10.includes(q) || c11.includes(q) || name.includes(q);
      })
      .slice(0, 3);

    return [
      { key: "pat", label: "Pacientes", icon: User, items: pats, kind: "patient" },
      { key: "rx", label: "Recetas", icon: FileText, items: rxs, kind: "prescription" },
      { key: "ap", label: "Citas", icon: Calendar, items: appts, kind: "appointment" },
      { key: "dx", label: "Diagnósticos", icon: Stethoscope, items: dxs, kind: "diagnosis" },
    ].filter((g) => g.items.length > 0);
  }, [input, patients, prescriptions, appointments, diagnoses, patientById]);

  const totalSuggest = suggestGroups.reduce((n, g) => n + g.items.length, 0);

  const pickSuggestion = (kind, item) => {
    setInput("");
    setShowSuggest(false);
    const patientId = kind === "patient" ? item.id : item.patient_id;
    const patient = patientId ? patientById.get(patientId) : null;
    // Receta primero (arriba visual) — luego el paciente debajo con su ficha.
    if (kind === "prescription") {
      pushWidget({ kind: "prescription_detail", prescription: item, patient });
    }
    if (patient) {
      setActivePatient(patient);
      pushWidget({ kind: "patient", patient });
    }
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("es", { day: "2-digit", month: "short" });
    } catch { return ""; }
  };

  // Resalta en <mark> el fragmento coincidente dentro de un texto libre.
  // Devuelve un array de spans para poder renderizarse como children React.
  const highlight = (text, q) => {
    if (!text) return null;
    const query = (q || "").trim();
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-[#01BC8E]/15 text-[#01BC8E] font-extrabold rounded px-0.5">
          {text.slice(idx, idx + query.length)}
        </mark>
        {text.slice(idx + query.length)}
      </>
    );
  };

  // Redirect si Aris no está habilitado
  useEffect(() => {
    if (user && !user.aris_enabled) {
      toast.error("Aris no está habilitado para tu cuenta.");
      navigate("/doctor");
    }
  }, [user, navigate]);

  // Focus del input al montar
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Auto-scroll a la parte inferior del stack cuando llega un widget
  useEffect(() => {
    stackRef.current?.scrollTo({
      top: stackRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [widgets.length]);

  const pushWidget = (w) =>
    setWidgets((prev) => [...prev, { id: Date.now() + Math.random(), ...w }]);

  // ---- Consulta en curso: session helpers ----
  const consultationStart = (patient) => {
    setConsultation({
      patient_id: patient.id,
      patient_name: patient.full_name,
      started_at: Date.now(),
      diagnosis: "",
      symptoms: "",
      treatment: "",
      notes: [],
      rxItems: [],
    });
  };
  const consultationEnd = () => setConsultation(null);
  const consultationSetField = (field, value) => {
    setConsultation((c) => {
      if (!c) return c;
      const patch = { [field]: value };
      // Si es diagnóstico y tenemos códigos CIE guardados del autosuggest,
      // adjuntarlos también.
      if (field === "diagnosis" && lastIcd) {
        patch.cie10 = lastIcd.cie10 || null;
        patch.cie11 = lastIcd.cie11 || null;
      }
      return { ...c, ...patch };
    });
  };
  const consultationAddNote = (text) => {
    setConsultation((c) =>
      c ? { ...c, notes: [...c.notes, text] } : c
    );
  };
  const consultationAddRxItem = (item) => {
    setConsultation((c) =>
      c ? { ...c, rxItems: [...(c.rxItems || []), item] } : c
    );
  };

  const closeAndExit = () => {
    navigate("/doctor");
  };

  const executeCommand = async (text) => {
    const msg = (text || "").trim();
    if (!msg) return;
    pushWidget({ kind: "user", text: msg });

    const parsed = parseCommand(msg);
    if (!parsed) {
      pushWidget({
        kind: "text",
        text: 'No reconocí ese comando. Escribe "ayuda" para ver la lista.',
      });
      return;
    }

    const result = await runParsedCommand(parsed, {
      search,
      activePatient,
      api,
      navigate: (path) => {
        navigate(path);
      },
      consultation,
      consultationStart,
      consultationEnd,
      consultationSetField,
      consultationAddNote,
      consultationAddRxItem,
    });

    if (!result) return;

    if (result.type === "reply") {
      pushWidget({ kind: "text", text: result.text });
    } else if (result.type === "widget_patient") {
      setActivePatient(result.patient);
      pushWidget({ kind: "patient", patient: result.patient });
    } else if (result.type === "choose_patient") {
      pushWidget({
        kind: "choose_patient",
        text: result.text,
        options: result.options,
      });
    } else if (result.type === "choose_patient_for_appt") {
      pushWidget({
        kind: "choose_patient_for_appt",
        text: result.text,
        options: result.options,
      });
    } else if (result.type === "help") {
      pushWidget({ kind: "help" });
    }
  };

  const send = async () => {
    const msg = input.trim();
    if (!msg) return;
    setInput("");
    await executeCommand(msg);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const onSuggestion = (s) => {
    setInput(s);
    inputRef.current?.focus();
  };

  const onChoosePatient = (opt, forAppt = false) => {
    if (forAppt) {
      navigate(`/doctor/agenda/nueva?patient_id=${opt.id}`);
    } else {
      const p = patients.find((x) => x.id === opt.id);
      if (p) {
        setActivePatient(p);
        pushWidget({ kind: "patient", patient: p });
      }
    }
  };

  const openPatientDetail = (pid) => {
    navigate(`/doctor/pacientes/${pid}`);
  };

  const suggestions = useMemo(
    () => [
      "muéstrame el paciente Juan",
      "crear paciente",
      "crear cita",
      "ayuda",
    ],
    []
  );

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-hidden" data-testid="aris-root">
      {/* Header — cerrar */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2 text-black">
          <span className="font-extrabold tracking-tight">Aris</span>
          <span className="text-xs text-black/40">· Comandos</span>
          <span
            data-testid="aris-cache-status"
            className={`ml-3 text-[10px] px-2 py-0.5 rounded-full font-bold ${
              cacheReady
                ? "bg-[#01BC8E]/15 text-[#01BC8E]"
                : "bg-amber-500/15 text-amber-600"
            }`}
          >
            {cacheReady
              ? `${patients.length} pacientes listos`
              : "Cargando cache…"}
          </span>
          {activePatient && (
            <span
              data-testid="aris-active-patient"
              className="ml-2 text-[10px] px-2 py-0.5 rounded-full font-bold bg-black/[0.06] text-black/70"
            >
              Activo: {activePatient.full_name}
            </span>
          )}
        </div>
        <button
          onClick={closeAndExit}
          data-testid="aris-close"
          className="w-10 h-10 rounded-xl bg-white hover:bg-black/[0.04] border border-black/10 shadow-sm flex items-center justify-center text-black transition"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Banner de consulta en curso — sticky bajo el header */}
      {consultation && (
        <ConsultationBanner
          consultation={consultation}
          onSave={() => executeCommand("guarda la consulta")}
          onCancel={() => executeCommand("cancela la consulta")}
        />
      )}

      {/* Layout centrado — fondo blanco, sin ondas ni hero, sólo la barra
       * pill con la esfera verde 3D a la izquierda. */}
      <div className="relative z-0 h-full flex flex-col items-center justify-center px-4">
        {/* Stack de widgets — arriba de la barra */}
        {widgets.length > 0 && (
          <div
            ref={stackRef}
            className="w-full max-w-6xl mb-6 flex flex-col gap-5 overflow-y-auto pr-1"
            style={{ maxHeight: "50vh" }}
          >
            {widgets.map((w) => (
              <WidgetRenderer
                key={w.id}
                w={w}
                onChoose={onChoosePatient}
                onOpenPatient={openPatientDetail}
                onSuggestion={onSuggestion}
                onNavigatePath={(path) => exitFsAndGo(navigate, path)}
                consultation={consultation}
              />
            ))}
          </div>
        )}

        {/* Barra centrada — pill blanco + LOGO plataforma + input Just ask */}
        <div className="w-full max-w-2xl relative">
          <div className="bg-white rounded-full border border-black/[0.06] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] flex items-center pl-3 pr-3 py-2.5">
            <img
              src="/brand/consudoctor-icon.png"
              alt="ConsuDoctor"
              className="w-12 h-12 flex-shrink-0 object-contain"
              data-testid="aris-brand-logo"
            />
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setShowSuggest(true);
              }}
              onFocus={() => setShowSuggest(true)}
              onKeyDown={onKey}
              placeholder="Just ask…"
              data-testid="aris-input"
              className="flex-1 bg-transparent px-4 py-2.5 outline-none text-black placeholder-black/35 text-lg font-medium"
              autoComplete="off"
            />
            <button
              onClick={send}
              disabled={!input.trim()}
              data-testid="aris-send"
              className="w-10 h-10 rounded-xl bg-[#01BC8E] hover:bg-[#019A75] text-white flex items-center justify-center disabled:opacity-30 shadow-md shadow-emerald-500/25 transition"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>

          {/* Dropdown de autosuggest — look premium con highlight */}
          {showSuggest && input.trim().length >= 2 && (
            <div
              className="absolute z-40 left-1/2 -translate-x-1/2 mt-3 w-[560px] max-w-[calc(100vw-2rem)] max-h-[440px] overflow-auto rounded-3xl bg-white shadow-[0_30px_80px_-15px_rgba(0,0,0,0.25)] border border-black/[0.06]"
              data-testid="aris-suggest"
            >
              {totalSuggest === 0 ? (
                <div className="px-6 py-8 text-center">
                  <div className="text-sm font-bold text-black/70 mb-1">Sin resultados</div>
                  <div className="text-xs text-black/45">Prueba con otro nombre o palabra clave.</div>
                </div>
              ) : (
                <div className="py-2">
                {suggestGroups.map((g, gi) => {
                  const Icon = g.icon;
                  const groupColor = {
                    patient: { bg: "bg-[#01BC8E]/10", text: "text-[#01BC8E]", accent: "bg-[#01BC8E]" },
                    prescription: { bg: "bg-[#01BC8E]/10", text: "text-[#019A75]", accent: "bg-[#01BC8E]" },
                    appointment: { bg: "bg-[#F59E0B]/10", text: "text-[#B45309]", accent: "bg-[#F59E0B]" },
                    diagnosis: { bg: "bg-[#8FE388]/15", text: "text-[#3F9E38]", accent: "bg-[#8FE388]" },
                  }[g.kind];
                  return (
                    <div key={g.key} data-testid={`aris-suggest-group-${g.key}`} className={gi > 0 ? "mt-1" : ""}>
                      <div className="flex items-center gap-2 px-5 pt-2 pb-1.5">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${groupColor.bg} ${groupColor.text} text-[10px] font-bold uppercase tracking-[0.14em]`}>
                          <Icon className="w-3 h-3" strokeWidth={2.4} weight="regular" />
                          {g.label}
                        </span>
                        <span className="ml-auto text-[10px] font-bold text-black/35">{g.items.length}</span>
                      </div>
                      {g.items.map((item) => {
                        const pat = item.patient_id ? patientById.get(item.patient_id) : null;
                        const patName = g.kind === "patient" ? item.full_name : pat?.full_name || "—";
                        const q = input.trim();
                        // Metadata contextual por tipo
                        let metaPrimary = null;
                        let metaSecondary = null;
                        if (g.kind === "patient") {
                          metaPrimary = item.phone || "Sin teléfono";
                          metaSecondary = item.document_id || null;
                        } else if (g.kind === "prescription") {
                          const meds = (item.items || [])
                            .map((it) => it.medication || it.medication_name)
                            .filter(Boolean)
                            .slice(0, 2)
                            .join(", ");
                          metaPrimary = meds || "Sin medicamentos";
                          metaSecondary = fmtDate(item.created_at || item.issued_at);
                        } else if (g.kind === "appointment") {
                          metaPrimary = item.reason || item.status || "Cita";
                          metaSecondary = fmtDate(item.starts_at || item.date);
                        } else if (g.kind === "diagnosis") {
                          metaPrimary = item.diagnosis;
                          metaSecondary = item.cie10 || item.cie11;
                        }
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => pickSuggestion(g.kind, item)}
                            className="group w-full text-left px-5 py-2.5 flex items-center gap-3 hover:bg-black/[0.03] transition"
                            data-testid={`aris-suggest-${g.kind}-${item.id}`}
                          >
                            <div className={`w-9 h-9 rounded-xl ${groupColor.accent} text-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
                              <Icon className="w-4 h-4" strokeWidth={2.2} weight="regular" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[14px] font-bold text-[#163144] truncate leading-tight">
                                {highlight(patName, q)}
                              </div>
                              {metaPrimary && (
                                <div className="text-[12px] text-black/55 truncate leading-tight mt-0.5">
                                  {highlight(metaPrimary, q)}
                                  {metaSecondary && (
                                    <span className="text-black/35 ml-1.5">· {metaSecondary}</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <span className="text-[11px] font-bold text-black/25 group-hover:text-[#01BC8E] transition flex-shrink-0">
                              ↵
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Atajos de teclado debajo de la barra — mostrar cuando input vacío */}
        {widgets.length === 0 && input.trim().length < 2 && (
          <div
            className="mt-6 flex items-center justify-center gap-6 text-xs text-black/45 font-medium select-none"
            data-testid="aris-shortcuts"
          >
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-black/[0.06] rounded font-bold text-[10px]">Enter</kbd>
              enviar
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-black/[0.06] rounded font-bold text-[10px]">↑ ↓</kbd>
              navegar
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="px-1.5 py-0.5 bg-black/[0.06] rounded font-bold text-[10px]">Esc</kbd>
              cerrar
            </span>
          </div>
        )}

        {/* Sugerencias CIE-10/11 cuando escribe "diagnóstico ..." */}
        <IcdInlineSuggestions
          value={input}
          onPick={(term, codes) => {
            setInput(`diagnóstico ${term}`);
            inputRef.current?.focus();
            setLastIcd(codes);
          }}
        />
      </div>
    </div>
  );
}

/**
 * Esfera verde 3D — reproduce el orb del mockup usando radial-gradients
 * apilados para el highlight y la sombra ambiente. Puramente CSS, sin
 * imágenes ni SVG.
 */
function AriseSphere() {
  return (
    <div className="relative w-14 h-14 flex-shrink-0" aria-hidden="true">
      {/* Sombra ambiente exterior */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(circle at 50% 55%, rgba(1,188,142,0.28) 0%, rgba(1,188,142,0) 60%)",
          transform: "scale(1.35)",
        }}
      />
      {/* Orbe */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: `
            radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 22%),
            radial-gradient(circle at 40% 35%, #7CE3B9 0%, #34C784 25%, #0EA96B 50%, #0B7A4C 75%, #033824 100%)
          `,
          boxShadow:
            "inset 6px 8px 14px rgba(255,255,255,0.35), inset -8px -10px 18px rgba(0,0,0,0.35), 0 8px 20px -6px rgba(0,60,30,0.35)",
        }}
      />
      {/* Highlight superior brillante */}
      <div
        className="absolute rounded-full"
        style={{
          top: "10%",
          left: "22%",
          width: "34%",
          height: "26%",
          background:
            "radial-gradient(ellipse at center, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 70%)",
          filter: "blur(1px)",
        }}
      />
    </div>
  );
}

// ==========================================================================
// Widget renderer
// ==========================================================================
function WidgetRenderer({ w, onChoose, onOpenPatient, onSuggestion, onNavigatePath, consultation }) {
  if (w.kind === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-5 py-2.5 rounded-full text-sm bg-[#01BC8E] text-white font-semibold shadow-lg shadow-emerald-500/30">
          {w.text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div className="w-full">
        {w.kind === "text" && <TextBubble text={w.text} />}
        {w.kind === "help" && <HelpCard onSuggestion={onSuggestion} />}
        {w.kind === "patient" && (
          <PatientCard
            patient={w.patient}
            onOpen={onOpenPatient}
            onNavigate={onNavigatePath}
            onSuggestion={onSuggestion}
            consultation={consultation}
          />
        )}
        {(w.kind === "choose_patient" ||
          w.kind === "choose_patient_for_appt") && (
          <ChoosePatientCard
            text={w.text}
            options={w.options}
            forAppt={w.kind === "choose_patient_for_appt"}
            onChoose={onChoose}
          />
        )}
        {w.kind === "prescription_detail" && (
          <PrescriptionDetailCard prescription={w.prescription} patient={w.patient} />
        )}
      </div>
    </div>
  );
}

/**
 * Widget de receta directa — muestra medicamentos + posología cuando el
 * usuario elige una receta específica desde el autosuggest. Se renderiza
 * encima del widget del paciente para dar contexto rápido.
 */
function PrescriptionDetailCard({ prescription, patient }) {
  const rx = prescription || {};
  const items = rx.items || [];
  const dateStr = (() => {
    const iso = rx.created_at || rx.issued_at;
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("es", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch {
      return null;
    }
  })();
  return (
    <div
      data-testid={`aris-prescription-card-${rx.id}`}
      className="bg-white rounded-3xl shadow-xl p-7 text-black border border-black/[0.04]"
    >
      <div className="flex items-center gap-4 mb-5">
        <div className="w-14 h-14 rounded-2xl bg-[#01BC8E]/15 text-[#01BC8E] flex items-center justify-center shrink-0">
          <FileText className="w-7 h-7" strokeWidth={1.8} weight="regular" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#01BC8E]">
            Receta médica
          </div>
          <div className="text-2xl font-extrabold text-[#163144] truncate leading-tight mt-0.5">
            {patient?.full_name || "Paciente"}
          </div>
          {dateStr && (
            <div className="text-sm text-black/55 mt-0.5">{dateStr}</div>
          )}
        </div>
        <a
          href={`/recetas/${rx.id}/imprimir`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`aris-prescription-print-${rx.id}`}
          className="px-4 py-2 rounded-full bg-black hover:bg-neutral-800 text-white text-xs font-bold shadow-md transition"
        >
          Imprimir / PDF
        </a>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-black/50 italic px-2">
          Esta receta no tiene medicamentos registrados.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((it, idx) => (
            <div
              key={idx}
              className="rounded-2xl border border-black/[0.06] bg-[#F8FAFB] p-4"
              data-testid={`aris-prescription-item-${idx}`}
            >
              <div className="flex items-baseline gap-3 flex-wrap">
                <div className="text-lg font-extrabold text-[#163144]">
                  {it.medication || it.medication_name || "Medicamento"}
                </div>
                {(it.dosage || it.dose) && (
                  <div className="text-sm font-bold text-[#01BC8E]">
                    {it.dosage || it.dose}
                  </div>
                )}
              </div>
              {(it.frequency || it.duration) && (
                <div className="mt-2 flex items-center gap-4 text-sm text-black/70">
                  {it.frequency && (
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-black/40 mr-1.5">
                        Frecuencia
                      </span>
                      {it.frequency}
                    </div>
                  )}
                  {it.duration && (
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-black/40 mr-1.5">
                        Duración
                      </span>
                      {it.duration}
                    </div>
                  )}
                </div>
              )}
              {it.instructions && (
                <div className="mt-2 text-sm text-black/75 bg-white rounded-xl p-2.5 border border-black/[0.04]">
                  <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-black/40 block mb-0.5">
                    Indicaciones
                  </span>
                  {it.instructions}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {rx.notes && (
        <div className="mt-4 text-sm text-black/70 bg-black/[0.03] rounded-xl p-3">
          <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-black/40 block mb-0.5">
            Notas adicionales
          </span>
          {rx.notes}
        </div>
      )}
    </div>
  );
}

function TextBubble({ text }) {
  return (
    <div className="inline-block px-5 py-2.5 rounded-2xl text-sm bg-white dark:bg-white/[0.06] text-black dark:text-white shadow-md">
      {text}
    </div>
  );
}

function HelpCard({ onSuggestion }) {
  return (
    <div className="bg-white dark:bg-white/[0.06] rounded-2xl shadow-md p-4 text-black dark:text-white">
      <div className="font-bold mb-3 text-sm">Comandos disponibles</div>
      <div className="grid gap-2">
        {HELP_COMMANDS.map((cmd) => (
          <div
            key={cmd.label}
            className="p-3 rounded-xl bg-black/[0.03] dark:bg-white/[0.04]"
          >
            <div className="font-semibold text-sm">{cmd.label}</div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {cmd.examples.map((ex) => (
                <button
                  key={ex}
                  onClick={() => onSuggestion(ex.replace(/^"|"$/g, ""))}
                  className="text-[11px] px-2 py-1 rounded-full bg-[#01BC8E]/10 text-[#01BC8E] hover:bg-[#01BC8E]/20 border border-[#01BC8E]/20 transition"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChoosePatientCard({ text, options, forAppt, onChoose }) {
  return (
    <div className="bg-white dark:bg-white/[0.06] rounded-2xl shadow-md p-4 text-black dark:text-white">
      <div className="text-sm mb-3">{text}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.id}
            data-testid={`aris-choose-${opt.id}`}
            onClick={() => onChoose(opt, forAppt)}
            className="px-3 py-1.5 rounded-full bg-[#01BC8E]/10 hover:bg-[#01BC8E]/20 text-[#01BC8E] text-xs font-bold border border-[#01BC8E]/30 transition"
          >
            {opt.label}
            {opt.sublabel && (
              <span className="ml-1 opacity-60">· {opt.sublabel}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function PatientCard({ patient, onOpen, onNavigate, onSuggestion, consultation }) {
  const age = ageFromBirth(patient.birth_date);
  const initials = (patient.full_name || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");

  const [nextAppt, setNextAppt] = useState(null);
  const [apptLoading, setApptLoading] = useState(true);
  const [prescriptions, setPrescriptions] = useState([]);
  const [presLoading, setPresLoading] = useState(true);
  const [diagnoses, setDiagnoses] = useState([]);
  const [dxLoading, setDxLoading] = useState(true);

  // Fetch en una sola llamada: /patients/{id} devuelve diagnoses,
  // appointments y prescriptions juntas — más eficiente que 3 requests.
  useEffect(() => {
    let cancelled = false;
    setApptLoading(true);
    setPresLoading(true);
    setDxLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/patients/${patient.id}`);
        if (cancelled) return;
        const now = Date.now();
        const futureForPatient = (data.appointments || [])
          .filter(
            (a) =>
              a.date &&
              new Date(a.date).getTime() >= now &&
              a.status !== "cancelled"
          )
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setNextAppt(futureForPatient[0] || null);

        const rxSorted = (data.prescriptions || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setPrescriptions(rxSorted.slice(0, 3));

        const dxSorted = (data.diagnoses || []).sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setDiagnoses(dxSorted.slice(0, 3));
      } catch {
        /* ignore */
      } finally {
        if (!cancelled) {
          setApptLoading(false);
          setPresLoading(false);
          setDxLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patient.id]);

  const openPrescription = (rxId) => onNavigate(`/recetas/${rxId}/imprimir`);

  return (
    <div
      data-testid={`aris-patient-card-${patient.id}`}
      className="bg-white dark:bg-white/[0.06] rounded-3xl shadow-xl p-7 text-black dark:text-white border border-black/[0.04] dark:border-white/[0.08]"
    >
      <div className="flex items-start gap-5">
        <div className="w-20 h-20 rounded-2xl bg-[#01BC8E]/15 text-[#01BC8E] flex items-center justify-center font-extrabold text-2xl shrink-0">
          {initials || <User className="w-8 h-8" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-2xl font-extrabold truncate leading-tight">
            {patient.full_name}
          </div>
          <div className="text-sm text-black/60 dark:text-white/60 mt-1">
            {patient.gender === "M"
              ? "Masculino"
              : patient.gender === "F"
                ? "Femenino"
                : "—"}
            {age != null ? ` · ${age} años` : ""}
            {patient.blood_type ? ` · ${patient.blood_type}` : ""}
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-2.5 text-sm">
            {patient.document_id && (
              <InfoRow icon={IdCard} label="Cédula" value={patient.document_id} />
            )}
            {patient.phone && (
              <InfoRow icon={Phone} label="Teléfono" value={patient.phone} />
            )}
            {patient.birth_date && (
              <InfoRow
                icon={Calendar}
                label="Nacimiento"
                value={patient.birth_date}
              />
            )}
            {patient.allergies && (
              <InfoRow
                icon={FileText}
                label="Alergias"
                value={patient.allergies}
              />
            )}
          </div>
          {patient.notes && (
            <div className="mt-3 text-xs text-black/70 dark:text-white/70 bg-black/[0.03] dark:bg-white/[0.04] p-2.5 rounded-xl max-h-24 overflow-y-auto whitespace-pre-line">
              {patient.notes}
            </div>
          )}
        </div>
      </div>

      {/* Sub-widgets: próxima cita + últimas recetas + últimas consultas */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <NextApptMini
          appt={nextAppt}
          loading={apptLoading}
          onCreate={() =>
            onNavigate(`/doctor/agenda/nueva?patient_id=${patient.id}`)
          }
        />
        <PrescriptionsMini
          items={prescriptions}
          loading={presLoading}
          onOpen={openPrescription}
        />
        <DiagnosesMini
          items={diagnoses}
          loading={dxLoading}
        />
      </div>

      {/* Quick actions dock */}
      <div
        data-testid={`aris-quick-actions-${patient.id}`}
        className="mt-4 flex flex-wrap gap-2"
      >
        {consultation?.patient_id === patient.id ? (
          <span
            data-testid={`aris-in-consultation-${patient.id}`}
            className="px-4 py-2 rounded-full bg-[#01BC8E]/15 text-[#01BC8E] text-xs font-bold border border-[#01BC8E]/30"
          >
            ● Consulta en curso
          </span>
        ) : (
          <button
            onClick={() => onSuggestion("inicia consulta")}
            data-testid={`aris-qa-start-consult-${patient.id}`}
            className="px-4 py-2 rounded-full bg-[#01BC8E] hover:bg-[#019A75] text-white text-xs font-bold shadow-md shadow-emerald-500/20 transition"
          >
            Iniciar consulta
          </button>
        )}
        <button
          onClick={() =>
            onNavigate(`/doctor/agenda/nueva?patient_id=${patient.id}`)
          }
          data-testid={`aris-qa-new-appt-${patient.id}`}
          className="px-4 py-2 rounded-full bg-black text-white hover:bg-[#01BC8E] text-xs font-bold shadow-md transition"
        >
          Crear cita
        </button>
        <button
          onClick={() =>
            onNavigate(`/doctor/recetas?patient_id=${patient.id}&new=1`)
          }
          data-testid={`aris-qa-new-rx-${patient.id}`}
          className="px-4 py-2 rounded-full bg-black text-white hover:bg-[#01BC8E] text-xs font-bold shadow-md transition"
        >
          Nueva receta
        </button>
        <button
          onClick={() => onSuggestion("agrega esta nota ")}
          data-testid={`aris-qa-add-note-${patient.id}`}
          className="px-4 py-2 rounded-full bg-white dark:bg-white/[0.10] text-black dark:text-white text-xs font-bold border border-black/[0.08] dark:border-white/[0.15] hover:bg-black/[0.04] dark:hover:bg-white/[0.15] transition"
        >
          Agregar nota
        </button>
        <button
          onClick={() => window.open(`/doctor/pacientes/${patient.id}`, "_blank", "noopener,noreferrer")}
          data-testid={`aris-open-patient-${patient.id}`}
          className="px-4 py-2 rounded-full bg-white dark:bg-white/[0.10] text-black dark:text-white text-xs font-bold border border-black/[0.08] dark:border-white/[0.15] hover:bg-black/[0.04] dark:hover:bg-white/[0.15] transition"
        >
          Abrir expediente
        </button>
      </div>
    </div>
  );
}

function NextApptMini({ appt, loading, onCreate }) {
  return (
    <div
      data-testid="aris-appt-widget"
      className="rounded-xl bg-black/[0.03] dark:bg-white/[0.04] p-3 text-xs text-black dark:text-white"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Calendar className="w-3.5 h-3.5 text-[#01BC8E]" />
        <span className="uppercase tracking-wider text-[10px] font-bold text-black/60 dark:text-white/60">
          Próxima cita
        </span>
      </div>
      {loading ? (
        <div className="text-black/50 dark:text-white/50">Cargando…</div>
      ) : appt ? (
        <div>
          <div className="font-semibold">{formatApptDate(appt.date)}</div>
          <div className="text-[11px] text-black/60 dark:text-white/60 mt-0.5">
            {appt.reason || "Sin motivo especificado"} · {appt.duration_min}m
            {appt.patient_confirmation === "confirmed" && (
              <span className="ml-1 text-[#01BC8E]">· confirmada</span>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="text-black/50 dark:text-white/50 mb-1.5">
            Sin citas futuras.
          </div>
          <button
            onClick={onCreate}
            className="text-[11px] text-[#01BC8E] font-bold hover:underline"
          >
            + Crear cita ahora
          </button>
        </div>
      )}
    </div>
  );
}

function PrescriptionsMini({ items, loading, onOpen }) {
  return (
    <div
      data-testid="aris-rx-widget"
      className="rounded-xl bg-black/[0.03] dark:bg-white/[0.04] p-3 text-xs text-black dark:text-white"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <FileText className="w-3.5 h-3.5 text-[#01BC8E]" />
        <span className="uppercase tracking-wider text-[10px] font-bold text-black/60 dark:text-white/60">
          Últimas recetas
        </span>
      </div>
      {loading ? (
        <div className="text-black/50 dark:text-white/50">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-black/50 dark:text-white/50">
          Sin recetas emitidas.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((rx) => {
            const first = (rx.items || [])[0] || {};
            const label =
              rx.diagnosis ||
              first.medication ||
              "Receta";
            return (
              <button
                key={rx.id}
                onClick={() => onOpen(rx.id)}
                data-testid={`aris-rx-item-${rx.id}`}
                className="w-full text-left flex items-start justify-between gap-2 py-1 border-b border-black/[0.05] dark:border-white/[0.06] last:border-0 hover:opacity-80"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{label}</div>
                  <div className="text-[10px] text-black/50 dark:text-white/50">
                    {(rx.items || []).length} medicamento
                    {(rx.items || []).length === 1 ? "" : "s"}
                    {rx.status ? ` · ${rx.status}` : ""}
                  </div>
                </div>
                <div className="text-[10px] text-black/50 dark:text-white/50 shrink-0">
                  {formatShortDate(rx.created_at)}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DiagnosesMini({ items, loading }) {
  return (
    <div
      data-testid="aris-dx-widget"
      className="rounded-xl bg-black/[0.03] dark:bg-white/[0.04] p-3 text-xs text-black dark:text-white"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <Stethoscope className="w-3.5 h-3.5 text-[#01BC8E]" />
        <span className="uppercase tracking-wider text-[10px] font-bold text-black/60 dark:text-white/60">
          Últimas consultas
        </span>
      </div>
      {loading ? (
        <div className="text-black/50 dark:text-white/50">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-black/50 dark:text-white/50">
          Sin consultas registradas.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((dx) => (
            <div
              key={dx.id}
              data-testid={`aris-dx-item-${dx.id}`}
              className="py-1 border-b border-black/[0.05] dark:border-white/[0.06] last:border-0"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-semibold truncate flex-1">
                  {dx.diagnosis || "Sin diagnóstico"}
                </div>
                <div className="text-[10px] text-black/50 dark:text-white/50 shrink-0">
                  {formatShortDate(dx.created_at)}
                </div>
              </div>
              {(dx.symptoms || dx.notes) && (
                <div className="text-[10px] text-black/60 dark:text-white/60 mt-0.5 line-clamp-2">
                  {dx.symptoms || dx.notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConsultationBanner({ consultation, onSave, onCancel }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setElapsed(Math.floor((Date.now() - consultation.started_at) / 1000)),
      1000
    );
    setElapsed(Math.floor((Date.now() - consultation.started_at) / 1000));
    return () => clearInterval(t);
  }, [consultation.started_at]);

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const hasFields =
    !!consultation.diagnosis?.trim() ||
    !!consultation.symptoms?.trim() ||
    !!consultation.treatment?.trim() ||
    consultation.notes.length > 0 ||
    (consultation.rxItems || []).length > 0;

  return (
    <div
      data-testid="aris-consultation-banner"
      className="absolute top-16 left-1/2 -translate-x-1/2 z-20 w-[min(680px,94%)] bg-[#01BC8E] text-white rounded-2xl shadow-2xl shadow-emerald-500/40 px-5 py-3 flex items-center gap-4"
    >
      <div className="flex items-center gap-2 shrink-0">
        <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
        <span className="text-xs font-bold uppercase tracking-wider">
          Consulta en curso
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-extrabold truncate">
          {consultation.patient_name}
        </div>
        <div className="text-[11px] opacity-90 truncate">
          {mm}:{ss}
          {consultation.diagnosis ? ` · Dx: ${consultation.diagnosis}` : ""}
          {consultation.notes.length
            ? ` · ${consultation.notes.length} nota${consultation.notes.length === 1 ? "" : "s"}`
            : ""}
          {(consultation.rxItems || []).length
            ? ` · ${consultation.rxItems.length} med${consultation.rxItems.length === 1 ? "" : "s"}`
            : ""}
        </div>
      </div>
      <button
        onClick={onSave}
        disabled={!hasFields}
        data-testid="aris-consult-save"
        className="px-3 py-1.5 rounded-xl bg-white text-[#019A75] text-xs font-extrabold hover:bg-white/90 disabled:opacity-50 transition"
      >
        Guardar
      </button>
      <button
        onClick={onCancel}
        data-testid="aris-consult-cancel"
        className="px-3 py-1.5 rounded-xl bg-black/20 text-white text-xs font-extrabold hover:bg-black/30 transition"
      >
        Cancelar
      </button>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-[#01BC8E] shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-black/50 dark:text-white/50 font-bold">
          {label}
        </div>
        <div className="truncate">{value}</div>
      </div>
    </div>
  );
}

/**
 * IcdInlineSuggestions — cuando el input empieza con "diagnóstico ", muestra
 * sugerencias CIE-10/11 debajo de la barra. Click autocompleta el input.
 */
function IcdInlineSuggestions({ value, onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastQ = useRef("");

  const query = (() => {
    const m = (value || "")
      .toLowerCase()
      .trim()
      .match(/^(?:diagnostico|diagnóstico|dx)[:,]?\s+(.+)$/);
    return m ? m[1].trim() : "";
  })();

  useEffect(() => {
    if (query.length < 2) {
      setItems([]);
      return;
    }
    const t = setTimeout(async () => {
      lastQ.current = query;
      setLoading(true);
      try {
        const { data } = await api.get(
          `/catalogs/icd?q=${encodeURIComponent(query)}&limit=6`
        );
        if (lastQ.current === query) setItems(data || []);
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [query]);

  if (query.length < 2) return null;
  if (!loading && items.length === 0) return null;

  return (
    <div
      data-testid="aris-icd-suggestions"
      className="w-full max-w-2xl mt-3 rounded-2xl bg-white/95 dark:bg-[#171717]/95 backdrop-blur-xl border border-black/[0.08] dark:border-white/[0.10] shadow-xl overflow-hidden"
    >
      <div className="px-4 py-2 border-b border-black/[0.05] dark:border-white/[0.06] flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider font-bold text-black/50 dark:text-white/50">
          Sugerencias CIE-10 / CIE-11
        </span>
        {loading && (
          <span className="w-3 h-3 border-2 border-black/20 border-t-[#01BC8E] rounded-full animate-spin" />
        )}
      </div>
      <div className="max-h-[260px] overflow-y-auto">
        {items.map((s, idx) => (
          <button
            key={`${s.term}-${idx}`}
            type="button"
            onClick={() =>
              onPick(s.term, { cie10: s.cie10, cie11: s.cie11 })
            }
            data-testid={`aris-icd-suggest-${idx}`}
            className="w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-black/[0.03] dark:hover:bg-white/[0.04] border-b border-black/[0.04] dark:border-white/[0.05] last:border-b-0 transition"
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-black dark:text-white truncate">
                {s.term}
              </div>
              <div className="text-[11px] text-black/50 dark:text-white/50 mt-0.5">
                CIE-10:{" "}
                <span className="font-mono font-semibold text-[#01BC8E]">
                  {s.cie10 || "—"}
                </span>
                {" · "}
                CIE-11:{" "}
                <span className="font-mono font-semibold text-[#5B7AFF]">
                  {s.cie11 || "—"}
                </span>
                {s.category ? ` · ${s.category}` : ""}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
